import type { Disposable } from "@/shared/contracts/lifecycle";

interface HoverChargeExecutionControllerOptions {
  readonly getDurationMs: () => number;
  readonly canExecute: () => boolean;
  readonly onProgress: (progress: number) => void;
  readonly onExecute: () => void;
  readonly now?: () => number;
  readonly requestAnimationFrame?: (callback: FrameRequestCallback) => number;
  readonly cancelAnimationFrame?: (handle: number) => void;
}

function defaultNow(): number {
  return globalThis.performance?.now() ?? Date.now();
}

function clampProgress(progress: number): number {
  return Math.max(0, Math.min(1, progress));
}

export class HoverChargeExecutionController implements Disposable {
  private readonly getDurationMs: () => number;
  private readonly canExecute: () => boolean;
  private readonly onProgress: (progress: number) => void;
  private readonly onExecute: () => void;
  private readonly now: () => number;
  private readonly requestFrame: (callback: FrameRequestCallback) => number;
  private readonly cancelFrame: (handle: number) => void;

  private progress = 0;
  private isHovered = false;
  private blockedUntilHoverLeaves = false;
  private isExecuting = false;
  private isDisposed = false;
  private lastTickMs: number | null = null;
  private frameId: number | null = null;
  private completionTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: HoverChargeExecutionControllerOptions) {
    this.getDurationMs = options.getDurationMs;
    this.canExecute = options.canExecute;
    this.onProgress = options.onProgress;
    this.onExecute = options.onExecute;
    this.now = options.now ?? defaultNow;
    this.requestFrame =
      options.requestAnimationFrame ??
      ((callback: FrameRequestCallback): number => {
        if (typeof globalThis.requestAnimationFrame === "function") {
          return globalThis.requestAnimationFrame(callback);
        }
        return setTimeout(() => callback(this.now()), 16) as unknown as number;
      });
    this.cancelFrame =
      options.cancelAnimationFrame ??
      ((handle: number): void => {
        if (typeof globalThis.cancelAnimationFrame === "function") {
          globalThis.cancelAnimationFrame(handle);
          return;
        }
        clearTimeout(handle as unknown as ReturnType<typeof setTimeout>);
      });
  }

  setHovered(isHovered: boolean): boolean {
    if (this.isDisposed) {
      return false;
    }
    this.advanceToNow();
    if (!isHovered) {
      this.isHovered = false;
      this.blockedUntilHoverLeaves = false;
      this.clearCompletionTimer();
      this.scheduleFrameIfNeeded();
      return false;
    }
    if (this.blockedUntilHoverLeaves || !this.canExecute()) {
      this.isHovered = false;
      this.lastTickMs = null;
      return false;
    }
    this.isHovered = true;
    if (this.getDurationMs() <= 0) {
      this.complete();
      return true;
    }
    this.lastTickMs = this.now();
    this.scheduleCompletionTimerIfNeeded();
    this.scheduleFrameIfNeeded();
    return true;
  }

  syncCanExecute(): void {
    if (this.isDisposed) {
      return;
    }
    if (!this.canExecute()) {
      this.cancel();
      return;
    }
    if (!this.isHovered || this.blockedUntilHoverLeaves) {
      return;
    }
    if (this.getDurationMs() <= 0) {
      this.complete();
      return;
    }
    this.lastTickMs ??= this.now();
    this.scheduleCompletionTimerIfNeeded();
    this.scheduleFrameIfNeeded();
  }

  cancel(): void {
    this.clearFrame();
    this.clearCompletionTimer();
    this.isHovered = false;
    this.blockedUntilHoverLeaves = false;
    this.lastTickMs = null;
    this.setProgress(0);
  }

  getProgress(): number {
    return this.progress;
  }

  dispose(): void {
    this.isDisposed = true;
    this.cancel();
  }

  private tick(): void {
    this.frameId = null;
    if (this.isDisposed || this.isExecuting) {
      return;
    }
    this.advanceToNow();
    this.scheduleFrameIfNeeded();
  }

  private advanceToNow(): void {
    if (this.lastTickMs === null) {
      this.lastTickMs = this.now();
      return;
    }

    if (!this.canExecute()) {
      this.cancel();
      return;
    }

    const nowMs = this.now();
    const elapsedMs = Math.max(0, nowMs - this.lastTickMs);
    this.lastTickMs = nowMs;
    if (elapsedMs <= 0) {
      return;
    }

    const durationMs = Math.max(0, this.getDurationMs());
    if (durationMs <= 0) {
      if (this.isHovered && !this.blockedUntilHoverLeaves) {
        this.complete();
      }
      return;
    }

    const delta = elapsedMs / durationMs;
    if (this.isHovered && !this.blockedUntilHoverLeaves) {
      this.setProgress(this.progress + delta);
      if (this.progress >= 1) {
        this.complete();
      }
      return;
    }

    if (this.progress > 0) {
      this.setProgress(this.progress - delta);
    }
  }

  private complete(): void {
    if (this.isDisposed || this.isExecuting || !this.canExecute()) {
      return;
    }
    this.isExecuting = true;
    this.clearFrame();
    this.clearCompletionTimer();
    this.setProgress(1);
    try {
      this.onExecute();
    } finally {
      this.isExecuting = false;
      this.blockedUntilHoverLeaves = this.isHovered;
      this.lastTickMs = null;
      this.setProgress(0);
    }
  }

  private scheduleFrameIfNeeded(): void {
    if (
      this.isDisposed ||
      this.isExecuting ||
      this.frameId !== null ||
      !this.shouldKeepTicking()
    ) {
      return;
    }
    this.frameId = this.requestFrame(() => this.tick());
  }

  private scheduleCompletionTimerIfNeeded(): void {
    if (
      this.isDisposed ||
      this.isExecuting ||
      this.completionTimer !== null ||
      !this.isHovered ||
      this.blockedUntilHoverLeaves ||
      !this.canExecute()
    ) {
      return;
    }
    const durationMs = Math.max(0, this.getDurationMs());
    if (durationMs <= 0) {
      return;
    }
    const remainingMs = Math.max(0, (1 - this.progress) * durationMs);
    this.completionTimer = setTimeout(() => {
      this.completionTimer = null;
      this.advanceToNow();
      this.scheduleFrameIfNeeded();
    }, remainingMs);
  }

  private shouldKeepTicking(): boolean {
    if (this.progress > 0) {
      return true;
    }
    return this.isHovered && !this.blockedUntilHoverLeaves && this.canExecute();
  }

  private clearFrame(): void {
    if (this.frameId === null) {
      return;
    }
    this.cancelFrame(this.frameId);
    this.frameId = null;
  }

  private clearCompletionTimer(): void {
    if (this.completionTimer === null) {
      return;
    }
    clearTimeout(this.completionTimer);
    this.completionTimer = null;
  }

  private setProgress(progress: number): void {
    const nextProgress = clampProgress(progress);
    if (nextProgress === this.progress) {
      return;
    }
    this.progress = nextProgress;
    this.onProgress(this.progress);
  }
}
